import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ExtensionGroups } from './react/ExtensionGroups';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { CheckoutReducer } from '@pages/checkout-app/checkout-store/reducers';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { ShopService } from '@app/services/shops/shop.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { AngularAction } from '../checkout/react/models/AngularAction';
import { ApiService } from '@app/core/services/api/api.service';
import { ExtensionsUsingIdInUrl, TypeToRouteSlugMap } from '../extensions/types';

enum AngularActions {
  TOAST = 'toast',
  SHOW_HIDE_MAIN_LOADER = 'show-hide-main-loader',
  EDIT_EXTENSION = 'edit-extension',
}

@Component({
  selector: 'extension-groups',
  template: `@if (isLoading) {
      <div>
        <app-main-loader-lottie> </app-main-loader-lottie>
      </div>
    }
    <div #extensionGroupsContainer></div>`,
  styleUrls: ['./extension-groups.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ExtensionGroupsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('extensionGroupsContainer', { static: false }) containerRef: ElementRef;
  root: ReactDOM.Root;
  destroyed$ = new Subject();
  angularRoutingSubject: Subject<string> = new Subject<string>();
  angularActionSubject: Subject<AngularAction> = new Subject<AngularAction>();
  isLoading = false;
  initialGroupId: number | null = null;

  constructor(
    private store: Store<fromCheckoutReducer.State>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private shopService: ShopService,
    private toastService: ToastService,
    private apiService: ApiService,
  ) {}

  ngOnInit(): void {
    const groupId = this.activatedRoute.snapshot.params['id'];
    this.initialGroupId = groupId ? Number(groupId) : null;
    this.store.dispatch(CheckoutActions.loadExtensionGroups());
    this.store.dispatch(CheckoutActions.loadExtensionsListLightweight());

    this.angularRoutingSubject.pipe(takeUntil(this.destroyed$)).subscribe((url) => {
      if (url) {
        this.router.navigateByUrl(url);
      }
    });

    this.angularActionSubject.pipe(takeUntil(this.destroyed$)).subscribe((action) => {
      if (action.action === AngularActions.TOAST) {
        this.toastService.showToast(action.payload);
        action.callback({});
      } else if (action.action === AngularActions.SHOW_HIDE_MAIN_LOADER) {
        this.isLoading = action.payload.show;
        action.callback({});
      } else if (action.action === AngularActions.EDIT_EXTENSION) {
        const { extensionId, groupId, extensionType, usageToken } = action.payload;
        const metricsFromDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10);
        const metricsToDate = new Date().toISOString().slice(0, 10);
        const routeSlug = TypeToRouteSlugMap[extensionType];

        if (routeSlug) {
          const useIdInUrl = ExtensionsUsingIdInUrl.includes(extensionType);
          const urlParam = useIdInUrl ? extensionId.toString() : usageToken;
          if (urlParam) {
            this.router.navigate(['/convert/extensions', routeSlug, urlParam], {
              queryParams: { fromGroup: groupId },
            });
            action.callback({});
            return;
          }
        }
        this.router.navigate(['/convert/extensions'], {
          queryParams: { fromGroup: groupId },
        });
        this.store.dispatch(
          CheckoutActions.loadExtensionById({
            shopId: this.shopService.shopId,
            metricsToDate,
            metricsFromDate,
            extensionId,
            extensionType,
          }),
        );
        action.callback({});
      }
    });
  }

  ngAfterViewInit() {
    this.render();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.root) {
      this.root.unmount();
    }
  }

  private render() {
    this.root = ReactDOM.createRoot(this.containerRef.nativeElement);
    this.root.render(
      <ExtensionGroups
        extensionGroups$={this.store.select(CheckoutReducer.selectExtensionGroups)}
        extensions$={this.store.select(CheckoutReducer.selectMerchantVisibleExtensions)}
        shop$={this.shopService.shop$}
        jwtToken={this.apiService.jwtToken}
        angularRoutingSubject={this.angularRoutingSubject}
        angularActionSubject={this.angularActionSubject}
        initialGroupId={this.initialGroupId}
      />,
    );
  }
}
